/**
 * Geometry of the virtualized mentions grid. The virtualizer lays out rows, so
 * the column count the CSS grid used to derive on its own (auto-fill) is
 * computed here from the measured width, with the same rule.
 */

/** Narrowest a card may get before the grid drops a column. */
const MIN_CARD_WIDTH_PX = 200;
/** Wide screens settle on this many readable cards instead of many tiny ones. */
const MAX_COLUMNS = 6;
/** The dense variant (contact drawer) always shows three tiles per row. */
const COMPACT_COLUMNS = 3;
/** Footer height under the media, by density (padding + avatar row). */
const FOOTER_HEIGHT_PX = { regular: 54, compact: 44 } as const;

export const MENTIONS_GRID_GAP_PX = {
  regular: { column: 12, row: 24 },
  compact: { column: 8, row: 12 },
} as const;

const density = (compact: boolean) => (compact ? 'compact' : 'regular');

/**
 * As many columns of at least 200px as fit, capped at 6. The virtualized grid
 * reads this; `cssGridTemplateColumns` states the same rule for a plain CSS grid.
 */
export const columnsForWidth = (width: number, compact: boolean): number => {
  if (compact) return COMPACT_COLUMNS;
  const gap = MENTIONS_GRID_GAP_PX.regular.column;
  const fitting = Math.floor((width + gap) / (MIN_CARD_WIDTH_PX + gap));
  return Math.min(MAX_COLUMNS, Math.max(1, fitting));
};

/**
 * `columnsForWidth` as a CSS template, for the few placeholder tiles shown
 * before the first page: each track is at least 200px and never narrower than
 * a sixth of the row minus the five gaps, so the browser settles on the same
 * count.
 */
export const cssGridTemplateColumns = (compact: boolean): string => {
  if (compact) return `repeat(${COMPACT_COLUMNS}, minmax(0, 1fr))`;
  const gaps = (MAX_COLUMNS - 1) * MENTIONS_GRID_GAP_PX.regular.column;
  return `repeat(auto-fill, minmax(max(${MIN_CARD_WIDTH_PX}px, (100% - ${gaps}px) / ${MAX_COLUMNS}), 1fr))`;
};

/** Parses a CSS aspect ratio ("4/5", "1") into width divided by height. */
const aspectRatioValue = (aspectRatio: string): number => {
  const [width, height = '1'] = aspectRatio.split('/');
  const ratio = Number(width) / Number(height);
  return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
};

/**
 * First guess of a row's height before it is measured: media at the card's
 * aspect ratio plus the footer. Only the scrollbar reads it; every rendered row
 * is measured and replaces the guess.
 */
export const estimateRowHeight = ({
  width,
  columns,
  compact,
  mediaAspectRatio,
}: {
  width: number;
  columns: number;
  compact: boolean;
  mediaAspectRatio: string;
}): number => {
  const { column } = MENTIONS_GRID_GAP_PX[density(compact)];
  const cardWidth = Math.max(MIN_CARD_WIDTH_PX, (width - (columns - 1) * column) / columns);
  return (
    Math.round(cardWidth / aspectRatioValue(mediaAspectRatio)) + FOOTER_HEIGHT_PX[density(compact)]
  );
};
